import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import { 
  Plus, 
  Trash2, 
  GripVertical, 
  Link as LinkIcon, 
  Music, 
  Youtube, 
  Mail, 
  Phone,
  User,
  Globe,
  Instagram,
  Twitter,
  Facebook,
  Loader2,
  Save,
  Eye,
  ExternalLink,
  Video,
  Share2
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const linkTypes = [
  { value: 'spotify', label: 'Spotify', icon: Music, placeholder: 'https://open.spotify.com/artist/...' },
  { value: 'youtube', label: 'YouTube Channel', icon: Youtube, placeholder: 'https://youtube.com/@channel' },
  { value: 'youtube_music', label: 'YouTube Music', icon: Youtube, placeholder: 'https://music.youtube.com/...' },
  { value: 'instagram', label: 'Instagram', icon: Instagram, placeholder: 'https://instagram.com/username' },
  { value: 'twitter', label: 'Twitter/X', icon: Twitter, placeholder: 'https://twitter.com/username' },
  { value: 'facebook', label: 'Facebook', icon: Facebook, placeholder: 'https://facebook.com/page' },
  { value: 'website', label: 'Website', icon: Globe, placeholder: 'https://yourwebsite.com' },
  { value: 'custom', label: 'Custom Link', icon: LinkIcon, placeholder: 'https://...' },
];

// Extract YouTube video ID
const getYouTubeVideoId = (url) => {
  if (!url) return null;
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\s?]+)/,
    /youtube\.com\/shorts\/([^&\s?]+)/
  ];
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) return match[1];
  }
  return null;
};

const BioLinkEditor = () => {
  const { user, token, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [videoDialogOpen, setVideoDialogOpen] = useState(false);
  
  const [bioData, setBioData] = useState({
    biography: '',
    contact_email: '',
    contact_phone: '',
    links: [],
    youtube_videos: [],
    background_gradient: 'from-black to-neutral-900',
    font_family: 'Oswald'
  });
  
  const [newLink, setNewLink] = useState({
    type: 'custom',
    title: '',
    url: ''
  });

  const [newVideo, setNewVideo] = useState({
    title: '',
    url: ''
  });
  
  const [draggedIndex, setDraggedIndex] = useState(null);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchBioLink();
  }, [isAuthenticated]);

  const fetchBioLink = async () => {
    try {
      const response = await axios.get(`${API}/bio-links/${user.username}`);
      if (response.data?.bio) {
        setBioData({
          biography: response.data.bio.biography || '',
          contact_email: response.data.bio.contact_email || user.email || '',
          contact_phone: response.data.bio.contact_phone || '',
          links: response.data.bio.links || [],
          youtube_videos: response.data.bio.youtube_videos || [],
          background_gradient: response.data.bio.background_gradient || 'from-black to-neutral-900',
          font_family: response.data.bio.font_family || 'Oswald'
        });
      } else {
        setBioData(prev => ({ ...prev, contact_email: user.email || '' }));
      }
    } catch (error) {
      setBioData(prev => ({ ...prev, contact_email: user.email || '' }));
    } finally {
      setLoading(false);
    }
  };

  const handleAddLink = () => {
    if (!newLink.title || !newLink.url) {
      toast.error('Please fill in both title and URL');
      return;
    }
    
    const linkType = linkTypes.find(t => t.value === newLink.type);
    setBioData({
      ...bioData,
      links: [...bioData.links, {
        id: Date.now().toString(),
        type: newLink.type,
        title: newLink.title,
        url: newLink.url,
        icon: linkType?.value || 'custom'
      }]
    });
    
    setNewLink({ type: 'custom', title: '', url: '' });
    setLinkDialogOpen(false);
    toast.success('Link added!');
  };

  const handleRemoveLink = (index) => {
    const updated = [...bioData.links];
    updated.splice(index, 1);
    setBioData({ ...bioData, links: updated });
  };

  const handleAddVideo = () => {
    if (!newVideo.url) {
      toast.error('Please enter a YouTube URL');
      return;
    }
    
    const videoId = getYouTubeVideoId(newVideo.url);
    if (!videoId) {
      toast.error('Invalid YouTube URL');
      return;
    }
    
    setBioData({
      ...bioData,
      youtube_videos: [...bioData.youtube_videos, {
        id: Date.now().toString(),
        title: newVideo.title || 'Video',
        url: newVideo.url,
        video_id: videoId
      }]
    });
    
    setNewVideo({ title: '', url: '' });
    setVideoDialogOpen(false);
    toast.success('Video added!');
  };

  const handleRemoveVideo = (index) => {
    const updated = [...bioData.youtube_videos];
    updated.splice(index, 1);
    setBioData({ ...bioData, youtube_videos: updated });
  };

  const handleDragStart = (index) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;
    
    const updated = [...bioData.links];
    const draggedItem = updated[draggedIndex];
    updated.splice(draggedIndex, 1);
    updated.splice(index, 0, draggedItem);
    
    setBioData({ ...bioData, links: updated });
    setDraggedIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/bio-links`, {
        links: bioData.links,
        biography: bioData.biography,
        contact_email: bioData.contact_email,
        contact_phone: bioData.contact_phone,
        youtube_videos: bioData.youtube_videos,
        background_gradient: bioData.background_gradient,
        font_family: bioData.font_family
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Bio.link saved successfully!');
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Failed to save bio.link');
    } finally {
      setSaving(false);
    }
  };

  const getIconComponent = (type) => {
    const linkType = linkTypes.find(t => t.value === type);
    return linkType?.icon || LinkIcon;
  };

  const profileUrl = `${window.location.origin}/${user?.username}`;

  const handleCopyLink = () => {
    navigator.clipboard.writeText(profileUrl);
    toast.success('Profile link copied!');
  };

  if (!isAuthenticated) {
    return null;
  }

  if (loading) {
    return (
      <Layout>
        <div className="min-h-[60vh] flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-m4-yellow" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="py-8 px-4 md:px-8">
        <div className="container mx-auto max-w-6xl">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
            <div>
              <p className="text-sm text-m4-yellow uppercase tracking-widest mb-2">Customize</p>
              <h1 className="font-heading text-4xl font-bold text-white">
                BIO.LINK EDITOR
              </h1>
              <p className="text-neutral-400 mt-1">
                Create your shareable profile page
              </p>
            </div>
            
            <div className="flex gap-3">
              {/* Share Link */}
              <div className="flex items-center gap-2 bg-neutral-800 border border-neutral-700 px-4 py-2">
                <span className="text-sm text-neutral-400 truncate max-w-[200px]">{profileUrl}</span>
                <Button 
                  size="sm" 
                  variant="ghost"
                  onClick={handleCopyLink}
                  className="text-m4-yellow hover:text-m4-yellow/80 p-1"
                  data-testid="copy-profile-link"
                >
                  <Share2 className="h-4 w-4" />
                </Button>
              </div>
              
              <a href={`/${user?.username}`} target="_blank" rel="noopener noreferrer">
                <Button variant="outline" className="border-neutral-700 text-white hover:bg-neutral-800">
                  <Eye className="h-4 w-4 mr-2" />
                  Preview
                </Button>
              </a>
              <Button 
                onClick={handleSave}
                disabled={saving}
                className="bg-m4-yellow text-black hover:bg-m4-yellow/90 font-bold"
                data-testid="save-biolink-btn"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                Save Changes
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Editor Panel */}
            <div className="space-y-6">
              {/* Biography */}
              <Card className="bg-neutral-900 border-neutral-800">
                <CardHeader>
                  <CardTitle className="font-heading text-lg text-white flex items-center gap-2">
                    <User className="h-5 w-5 text-m4-yellow" />
                    Biography
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <Textarea
                    value={bioData.biography}
                    onChange={(e) => setBioData({ ...bioData, biography: e.target.value })}
                    placeholder="Tell your story... Share your ministry journey, musical influences, and vision."
                    rows={5}
                    className="bg-neutral-800 border-neutral-700 rounded-xl text-white placeholder:text-neutral-500 focus:border-m4-yellow"
                    data-testid="biography-input"
                  />
                  <p className="text-xs text-neutral-500 mt-2">{bioData.biography.length}/500 characters</p>
                </CardContent>
              </Card>

              {/* Contact Info */}
              <Card className="bg-neutral-900 border-neutral-800">
                <CardHeader>
                  <CardTitle className="font-heading text-lg text-white flex items-center gap-2">
                    <Phone className="h-5 w-5 text-m4-yellow" />
                    Contact Information
                    <span className="text-xs text-neutral-500 font-normal ml-2">(Visible to logged-in users)</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label className="text-neutral-300">Email Address</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-500" />
                      <Input
                        type="email"
                        value={bioData.contact_email}
                        onChange={(e) => setBioData({ ...bioData, contact_email: e.target.value })}
                        placeholder="contact@example.com"
                        className="pl-10 bg-neutral-800 border-neutral-700 rounded-xl text-white focus:border-m4-yellow"
                        data-testid="contact-email-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-neutral-300">Phone Number</Label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-500" />
                      <Input
                        type="tel"
                        value={bioData.contact_phone}
                        onChange={(e) => setBioData({ ...bioData, contact_phone: e.target.value })}
                        placeholder="+91 98765 43210"
                        className="pl-10 bg-neutral-800 border-neutral-700 rounded-xl text-white focus:border-m4-yellow"
                        data-testid="contact-phone-input"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Tabs for Links and Videos */}
              <Card className="bg-neutral-900 border-neutral-800">
                <Tabs defaultValue="links" className="w-full">
                  <CardHeader className="pb-0">
                    <TabsList className="bg-neutral-800 w-full">
                      <TabsTrigger value="links" className="flex-1 data-[state=active]:bg-m4-yellow data-[state=active]:text-black">
                        <LinkIcon className="h-4 w-4 mr-2" />
                        Social Links
                      </TabsTrigger>
                      <TabsTrigger value="videos" className="flex-1 data-[state=active]:bg-m4-yellow data-[state=active]:text-black">
                        <Video className="h-4 w-4 mr-2" />
                        YouTube Videos
                      </TabsTrigger>
                    </TabsList>
                  </CardHeader>
                  
                  <CardContent className="pt-4">
                    {/* Links Tab */}
                    <TabsContent value="links" className="mt-0">
                      <div className="flex justify-end mb-4">
                        <Dialog open={linkDialogOpen} onOpenChange={setLinkDialogOpen}>
                          <DialogTrigger asChild>
                            <Button size="sm" className="bg-m4-yellow text-black hover:bg-m4-yellow/90" data-testid="add-link-btn">
                              <Plus className="h-4 w-4 mr-1" />
                              Add Link
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="bg-neutral-900 border-neutral-800">
                            <DialogHeader>
                              <DialogTitle className="font-heading text-xl text-white">Add Social Link</DialogTitle>
                            </DialogHeader>
                            <div className="space-y-4">
                              <div className="space-y-2">
                                <Label className="text-neutral-300">Link Type</Label>
                                <Select value={newLink.type} onValueChange={(value) => setNewLink({ ...newLink, type: value })}>
                                  <SelectTrigger className="bg-neutral-800 border-neutral-700 rounded-xl text-white">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent className="bg-neutral-900 border-neutral-800">
                                    {linkTypes.map((type) => {
                                      const Icon = type.icon;
                                      return (
                                        <SelectItem key={type.value} value={type.value} className="text-white">
                                          <div className="flex items-center gap-2">
                                            <Icon className="h-4 w-4" />
                                            {type.label}
                                          </div>
                                        </SelectItem>
                                      );
                                    })}
                                  </SelectContent>
                                </Select>
                              </div>
                              <div className="space-y-2">
                                <Label className="text-neutral-300">Title</Label>
                                <Input
                                  value={newLink.title}
                                  onChange={(e) => setNewLink({ ...newLink, title: e.target.value })}
                                  placeholder="My Spotify Profile"
                                  className="bg-neutral-800 border-neutral-700 rounded-xl text-white focus:border-m4-yellow"
                                  data-testid="link-title-input"
                                />
                              </div>
                              <div className="space-y-2">
                                <Label className="text-neutral-300">URL</Label>
                                <Input
                                  value={newLink.url}
                                  onChange={(e) => setNewLink({ ...newLink, url: e.target.value })}
                                  placeholder={linkTypes.find(t => t.value === newLink.type)?.placeholder}
                                  className="bg-neutral-800 border-neutral-700 rounded-xl text-white focus:border-m4-yellow"
                                  data-testid="link-url-input"
                                />
                              </div>
                              <Button 
                                onClick={handleAddLink}
                                className="w-full bg-m4-yellow text-black hover:bg-m4-yellow/90 font-bold"
                                data-testid="confirm-add-link-btn"
                              >
                                Add Link
                              </Button>
                            </div>
                          </DialogContent>
                        </Dialog>
                      </div>

                      {bioData.links.length > 0 ? (
                        <div className="space-y-2">
                          {bioData.links.map((link, index) => {
                            const Icon = getIconComponent(link.type);
                            return (
                              <div
                                key={link.id || index}
                                draggable
                                onDragStart={() => handleDragStart(index)}
                                onDragOver={(e) => handleDragOver(e, index)}
                                onDragEnd={handleDragEnd}
                                className={`flex items-center gap-3 p-3 bg-neutral-800 border border-neutral-700 cursor-move hover:border-m4-yellow/50 transition-colors ${
                                  draggedIndex === index ? 'opacity-50' : ''
                                }`}
                                data-testid={`link-item-${index}`}
                              >
                                <GripVertical className="h-4 w-4 text-neutral-500 flex-shrink-0" />
                                <div className="h-10 w-10 bg-m4-yellow/10 flex items-center justify-center flex-shrink-0">
                                  <Icon className="h-5 w-5 text-m4-yellow" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="font-medium text-white truncate">{link.title}</p>
                                  <p className="text-xs text-neutral-500 truncate">{link.url}</p>
                                </div>
                                <button
                                  onClick={() => handleRemoveLink(index)}
                                  className="p-2 text-neutral-400 hover:text-red-500 transition-colors"
                                  data-testid={`remove-link-${index}`}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="text-center py-8 text-neutral-500">
                          <LinkIcon className="h-12 w-12 mx-auto mb-3 opacity-30" />
                          <p>No links added yet</p>
                          <p className="text-sm">Add your Spotify, YouTube, Instagram links</p>
                        </div>
                      )}
                    </TabsContent>

                    {/* Videos Tab */}
                    <TabsContent value="videos" className="mt-0">
                      <div className="flex justify-end mb-4">
                        <Dialog open={videoDialogOpen} onOpenChange={setVideoDialogOpen}>
                          <DialogTrigger asChild>
                            <Button size="sm" className="bg-m4-yellow text-black hover:bg-m4-yellow/90" data-testid="add-video-btn">
                              <Plus className="h-4 w-4 mr-1" />
                              Add YouTube Video
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="bg-neutral-900 border-neutral-800">
                            <DialogHeader>
                              <DialogTitle className="font-heading text-xl text-white">Add YouTube Video</DialogTitle>
                            </DialogHeader>
                            <div className="space-y-4">
                              <div className="space-y-2">
                                <Label className="text-neutral-300">Video Title (Optional)</Label>
                                <Input
                                  value={newVideo.title}
                                  onChange={(e) => setNewVideo({ ...newVideo, title: e.target.value })}
                                  placeholder="My Worship Session"
                                  className="bg-neutral-800 border-neutral-700 rounded-xl text-white focus:border-m4-yellow"
                                  data-testid="video-title-input"
                                />
                              </div>
                              <div className="space-y-2">
                                <Label className="text-neutral-300">YouTube URL</Label>
                                <Input
                                  value={newVideo.url}
                                  onChange={(e) => setNewVideo({ ...newVideo, url: e.target.value })}
                                  placeholder="https://youtube.com/watch?v=..."
                                  className="bg-neutral-800 border-neutral-700 rounded-xl text-white focus:border-m4-yellow"
                                  data-testid="video-url-input"
                                />
                                <p className="text-xs text-neutral-500">
                                  Supports: youtube.com/watch, youtu.be, youtube.com/shorts
                                </p>
                              </div>
                              {newVideo.url && getYouTubeVideoId(newVideo.url) && (
                                <div className="aspect-video bg-neutral-800">
                                  <iframe
                                    src={`https://www.youtube.com/embed/${getYouTubeVideoId(newVideo.url)}`}
                                    title="Preview"
                                    className="w-full h-full"
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                  />
                                </div>
                              )}
                              <Button 
                                onClick={handleAddVideo}
                                className="w-full bg-m4-yellow text-black hover:bg-m4-yellow/90 font-bold"
                                data-testid="confirm-add-video-btn"
                              >
                                Add Video
                              </Button>
                            </div>
                          </DialogContent>
                        </Dialog>
                      </div>

                      {bioData.youtube_videos.length > 0 ? (
                        <div className="space-y-3">
                          {bioData.youtube_videos.map((video, index) => (
                            <div
                              key={video.id || index}
                              className="bg-neutral-800 border border-neutral-700 overflow-hidden"
                              data-testid={`video-item-${index}`}
                            >
                              <div className="flex items-center justify-between p-3 border-b border-neutral-700">
                                <div className="flex items-center gap-2">
                                  <Youtube className="h-5 w-5 text-red-500" />
                                  <span className="font-medium text-white">{video.title}</span>
                                </div>
                                <button
                                  onClick={() => handleRemoveVideo(index)}
                                  className="p-2 text-neutral-400 hover:text-red-500 transition-colors"
                                  data-testid={`remove-video-${index}`}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </div>
                              <div className="aspect-video">
                                <iframe
                                  src={`https://www.youtube.com/embed/${video.video_id || getYouTubeVideoId(video.url)}`}
                                  title={video.title}
                                  className="w-full h-full"
                                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                />
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-8 text-neutral-500">
                          <Youtube className="h-12 w-12 mx-auto mb-3 opacity-30" />
                          <p>No videos added yet</p>
                          <p className="text-sm">Embed your YouTube videos to showcase your music</p>
                        </div>
                      )}
                    </TabsContent>
                  </CardContent>
                </Tabs>
              </Card>
            </div>

            {/* Live Preview */}
            <div className="lg:sticky lg:top-24">
              <Card className="bg-neutral-900 border-neutral-800 overflow-hidden">
                <CardHeader className="bg-neutral-800 border-b border-neutral-700">
                  <CardTitle className="font-heading text-lg text-white flex items-center justify-between">
                    <span>Live Preview</span>
                    <span className="text-xs text-neutral-400 font-normal">m4manna.com/{user?.username}</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  {/* Preview Frame */}
                  <div className="bg-gradient-to-b from-neutral-900 to-black min-h-[600px] p-6 overflow-y-auto max-h-[700px]">
                    {/* Profile Header */}
                    <div className="text-center mb-6">
                      <div className="h-20 w-20 rounded-full bg-m4-yellow mx-auto mb-3 flex items-center justify-center">
                        {user?.profile_image ? (
                          <img src={user.profile_image} alt="" className="w-full h-full rounded-full object-cover" />
                        ) : (
                          <span className="text-2xl font-heading font-bold text-black">
                            {user?.full_name?.charAt(0)}
                          </span>
                        )}
                      </div>
                      <h2 className="font-heading text-xl font-bold text-white">{user?.full_name}</h2>
                      <p className="text-neutral-400 text-sm">@{user?.username}</p>
                    </div>

                    {/* Biography */}
                    {bioData.biography && (
                      <div className="mb-6 text-center">
                        <p className="text-neutral-300 text-sm leading-relaxed">{bioData.biography}</p>
                      </div>
                    )}

                    {/* Contact Buttons */}
                    {(bioData.contact_email || bioData.contact_phone) && (
                      <div className="flex justify-center gap-2 mb-6">
                        {bioData.contact_email && (
                          <div className="flex items-center gap-2 bg-neutral-800 border border-neutral-700 px-3 py-2 text-xs text-white">
                            <Mail className="h-3 w-3 text-m4-yellow" />
                            Email
                          </div>
                        )}
                        {bioData.contact_phone && (
                          <div className="flex items-center gap-2 bg-neutral-800 border border-neutral-700 px-3 py-2 text-xs text-white">
                            <Phone className="h-3 w-3 text-m4-yellow" />
                            Call
                          </div>
                        )}
                      </div>
                    )}

                    {/* Links */}
                    {bioData.links.length > 0 && (
                      <div className="space-y-2 mb-6">
                        {bioData.links.map((link, index) => {
                          const Icon = getIconComponent(link.type);
                          return (
                            <div
                              key={index}
                              className="flex items-center gap-3 bg-neutral-800/80 border border-neutral-700 p-3 text-sm"
                            >
                              <Icon className="h-4 w-4 text-m4-yellow" />
                              <span className="flex-1 font-medium text-white truncate">{link.title}</span>
                              <ExternalLink className="h-3 w-3 text-neutral-500" />
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* YouTube Videos Preview */}
                    {bioData.youtube_videos.length > 0 && (
                      <div className="space-y-3 mb-6">
                        <p className="text-xs text-m4-yellow uppercase tracking-wider flex items-center gap-2">
                          <Youtube className="h-4 w-4" />
                          Videos
                        </p>
                        {bioData.youtube_videos.slice(0, 2).map((video, index) => (
                          <div key={index} className="bg-neutral-800 border border-neutral-700 overflow-hidden">
                            <p className="p-2 text-xs font-medium text-white border-b border-neutral-700 truncate">
                              {video.title}
                            </p>
                            <div className="aspect-video bg-neutral-900 flex items-center justify-center">
                              <Youtube className="h-8 w-8 text-red-500 opacity-50" />
                            </div>
                          </div>
                        ))}
                        {bioData.youtube_videos.length > 2 && (
                          <p className="text-xs text-neutral-500 text-center">
                            +{bioData.youtube_videos.length - 2} more videos
                          </p>
                        )}
                      </div>
                    )}

                    {/* Footer */}
                    <div className="mt-6 text-center">
                      <p className="text-xs text-neutral-500">Powered by M4manna</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default BioLinkEditor;
